import { useMemo, useState } from "react";
import { Popover } from "@mui/material";
import {
  GenericOrderLine,
  OrderLineHideColumns,
} from "./OrderLineData";
import {
  extractTypedValue,
  OrderLineGroup,
} from "./groupOrderLinesForDisplay";
import { OrderLine } from "../../../../core/models/entities/Order";

// NumberLogos can reach an order line in two shapes:
//
//  * Design-instance path (useOrderActionsHandler emitInstanceLogoLine): one
//    OrderLine per number with quantity = pieces × digits.length so pricing
//    tiers reflect per-digit applications. typedNumberValue is undefined; the
//    full number is in the title's "(...)" suffix.
//  * Standalone-logo path (useOrderActionsHandler emitLine in the digit split
//    branch): one OrderLine per digit, each with quantity = pieces (no digit
//    multiplier). typedNumberValue carries the original full number.
//
// For customer-facing display we always want the per-piece count, so divide by
// digit count only in the first case.
function getDisplayQuantity(line: OrderLine, isNumber: boolean): number {
  const raw = line.quantity ?? 0;
  if (!isNumber) return raw;
  if (line.typedNumberValue) return raw;
  const value = extractTypedValue(line);
  const digitCount = value && /^\d+$/.test(value) ? value.length : 1;
  if (digitCount <= 1) return raw;
  return Math.max(1, Math.round(raw / digitCount));
}

interface OrderLineGroupRowProps {
  group: OrderLineGroup;
  hide?: OrderLineHideColumns;
  currency: string;
  isFirstItem: boolean;
  showCustomerPrices?: boolean;
  readonly?: boolean;
}

function countVisibleColumns(hide?: OrderLineHideColumns): number {
  const cols: (keyof OrderLineHideColumns)[] = [
    "displayNo",
    "image",
    "itemNumber",
    "title",
    "type",
    "unitPrice",
    "quantity",
    "price",
    "subTotal",
  ];
  return cols.reduce((n, key) => (hide?.[key] ? n : n + 1), 0);
}

function isNumberVariant(group: OrderLineGroup): boolean {
  const value = extractTypedValue(group.variants[0]);
  return !!value && /^\d+$/.test(value);
}

function useSortedVariants(group: OrderLineGroup, isNumber: boolean) {
  return useMemo(() => {
    // Standalone NumberLogos emit one OrderLine per digit (e.g. "12" → two
    // lines for "1" and "2"), all sharing the same typedNumberValue. Collapse
    // them into one entry per typed value before sorting/displaying so the
    // popover shows "12 ×N" once instead of "1 ×N" and "2 ×N".
    const seen = new Map<string, OrderLine>();
    for (const v of group.variants) {
      const tv = extractTypedValue(v);
      const key = tv ?? `__no_typed__:${v.id}`;
      if (!seen.has(key)) seen.set(key, v);
    }
    const deduped = Array.from(seen.values());
    if (isNumber) {
      deduped.sort(
        (a, b) =>
          parseInt(extractTypedValue(a) ?? "0", 10) -
          parseInt(extractTypedValue(b) ?? "0", 10),
      );
    } else {
      deduped.sort((a, b) =>
        (extractTypedValue(a) ?? "")
          .toLowerCase()
          .localeCompare((extractTypedValue(b) ?? "").toLowerCase()),
      );
    }
    return deduped;
  }, [group.variants, isNumber]);
}

function VariantsContent({
  group,
  isNumber,
}: {
  group: OrderLineGroup;
  isNumber: boolean;
}) {
  const sorted = useSortedVariants(group, isNumber);
  const label = isNumber ? "Typed numbers" : "Typed names";

  return (
    <div className="min-w-[280px] max-w-[520px] p-4">
      <div className="flex items-baseline justify-between mb-3 pb-2 border-b border-slate-100">
        <span className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">
          {label}
        </span>
        <span className="text-[11px] tabular-nums text-slate-400">
          {sorted.length} total
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5 max-h-[320px] overflow-y-auto pr-1">
        {sorted.map((v) => (
          <span
            key={v.id}
            className="inline-flex items-baseline gap-1.5 bg-slate-100 rounded-md px-2.5 py-1 text-xs whitespace-nowrap"
          >
            <span className="text-slate-800 font-medium">
              {extractTypedValue(v) ?? "—"}
            </span>
            <span className="text-slate-400 tabular-nums">
              ×{getDisplayQuantity(v, isNumber)}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

function VariantsPrintList({
  group,
  isNumber,
}: {
  group: OrderLineGroup;
  isNumber: boolean;
}) {
  const sorted = useSortedVariants(group, isNumber);
  const label = isNumber ? "Typed numbers" : "Typed names";
  return (
    <div className="pl-16 pr-8 py-4">
      <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-600 mb-2">
        {label} · {sorted.length}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-800">
        {sorted.map((v, i) => (
          <span key={v.id} className="whitespace-nowrap">
            {extractTypedValue(v) ?? "—"}
            <span className="text-slate-500"> ×{getDisplayQuantity(v, isNumber)}</span>
            {i < sorted.length - 1 && <span className="text-slate-400">  ·</span>}
          </span>
        ))}
      </div>
    </div>
  );
}

export function OrderLineGroupRow({
  group,
  hide,
  currency,
  isFirstItem,
  showCustomerPrices,
  readonly,
}: OrderLineGroupRowProps) {
  const hasVariants = group.variants.length > 1;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (!hasVariants) {
    return (
      <GenericOrderLine
        orderline={group.leader}
        hide={hide}
        currency={currency}
        isFirstItem={isFirstItem}
        showCustomerPrices={showCustomerPrices}
      />
    );
  }

  // In read-only mode (viewing a placed order) we still collapse the duplicate
  // lines into one row per SKU x design position, but we don't surface a badge
  // or popover because typed values aren't reliably stored for past orders.
  if (readonly) {
    return (
      <GenericOrderLine
        orderline={group.leader}
        hide={hide}
        currency={currency}
        isFirstItem={isFirstItem}
        showCustomerPrices={showCustomerPrices}
      />
    );
  }

  const isNumber = isNumberVariant(group);
  // Count distinct typed values rather than raw variants — digit-split
  // NumberLogo lines collapse to one entry per typed number in the popover.
  const dedupedTypedValues = new Set(
    group.variants
      .map((v) => extractTypedValue(v))
      .filter((tv): tv is string => tv != null),
  );
  const count =
    dedupedTypedValues.size > 0
      ? dedupedTypedValues.size
      : group.variants.length;
  const badgeLabel = `${count} ${isNumber ? (count === 1 ? "number" : "numbers") : count === 1 ? "name" : "names"}`;

  const badge = (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
      }}
      className="inline-flex items-baseline gap-1 ml-3 px-2 py-0.5 rounded text-[11px] font-medium text-slate-500 border border-slate-200 hover:text-slate-900 hover:border-slate-400 hover:bg-slate-50 transition-colors print:hidden"
    >
      <span className="tabular-nums">{count}</span>
      <span className="font-normal">{isNumber ? (count === 1 ? "number" : "numbers") : count === 1 ? "name" : "names"}</span>
    </button>
  );

  const colSpan = countVisibleColumns(hide);

  return (
    <>
      <GenericOrderLine
        orderline={group.leader}
        hide={hide}
        currency={currency}
        isFirstItem={isFirstItem}
        showCustomerPrices={showCustomerPrices}
        trailingNote={badge}
      />

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            className: "shadow-lg border border-slate-200",
          },
        }}
      >
        <VariantsContent group={group} isNumber={isNumber} />
      </Popover>

      {/* Print-only: full list is always printed regardless of popover state */}
      <tr className="hidden print:table-row">
        <td colSpan={colSpan} className="p-0">
          <VariantsPrintList group={group} isNumber={isNumber} />
        </td>
      </tr>
    </>
  );
}
